import React from 'react';
import { Controller } from 'react-hook-form';
import { useState, useEffect as useEffectReact, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import ReactForm from '../../components/ReactForm';
import ReactDropdown from '../../components/ReactDropdown';
import { paths } from '../../routes/helpers';
import { createKeywordCategory, updateKeywordCategory, fetchKeywordCategoryById } from './keywordCategorySlice';
import { useEffect } from 'react';
import requests from '../../api/requests';

export default function KeywordCategoryCreateEdit() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { keywordCategoryId } = useParams();
  const loading = useSelector((state) => state.keywordCategory.loading);
  const error = useSelector((state) => state.keywordCategory.error);
  const item = useSelector((state) => keywordCategoryId ? state.keywordCategory.items.find(i => i._id === keywordCategoryId) : null);

  useEffect(() => {
    if (keywordCategoryId) {
      dispatch(fetchKeywordCategoryById(keywordCategoryId));
    }
  }, [keywordCategoryId, dispatch]);

  const handleSubmit = async (formData) => {
    try {
      // Transform relation data to send only IDs (not objects)
      const submitData = { ...formData };
      if (submitData.relation) {
        const relation = { ...submitData.relation };
        
        // Extract ID from location if it's an object
        if (relation.location) {
          relation.location = relation.location._id || relation.location;
        }
        
        // Extract ID from region if it's an object
        if (relation.region) {
          relation.region = relation.region._id || relation.region;
        }
        
        if (relation.neighbourhood) {
          relation.neighbourhood = relation.neighbourhood._id || relation.neighbourhood;
        }
        
        submitData.relation = relation;
      }
      
      if (keywordCategoryId) {
        await dispatch(updateKeywordCategory({ id: keywordCategoryId, data: submitData })).unwrap();
        toast.success('Keyword category updated!');
      } else {
        await dispatch(createKeywordCategory(submitData)).unwrap();
        toast.success('Keyword category created!');
      }
      navigate(paths.private.keywordCategory.list());
    } catch (err) {
      toast.error(err || 'Failed to save');
    }
  };

  const RelationGeoRow = ({ control, name }) => {
    const [neighbourhoodOptions, setNeighbourhoodOptions] = useState([]);
    const [neighbourhoodPage, setNeighbourhoodPage] = useState(1);
    const [neighbourhoodPages, setNeighbourhoodPages] = useState(1);
    const [neighbourhoodLoading, setNeighbourhoodLoading] = useState(false);
    const neighbourhoodSearchTimeoutRef = useRef(null);

    const fetchNeighbourhoods = async (page = 1, search = '', reset = false) => {
      setNeighbourhoodLoading(true);
      try {
        const params = { page, limit: 10 };
        if (search?.trim()) params.q = search.trim();
        const { data } = await requests.geo.neighbourhoods.search(params);
        const payload = data?.data || data || {};
        const list = Array.isArray(payload.neighbourhoods) ? payload.neighbourhoods : [];
        const newOptions = list.map((n) => ({ value: n._id, label: n.label || n.name }));
        setNeighbourhoodOptions((prev) => (reset ? newOptions : [...prev, ...newOptions]));
        setNeighbourhoodPages(payload.pages || 1);
        setNeighbourhoodPage(page);
      } catch (e) {
        if (reset) setNeighbourhoodOptions([]);
      } finally {
        setNeighbourhoodLoading(false);
      }
    };

    useEffectReact(() => {
      fetchNeighbourhoods(1, '', true);
    }, []);

    useEffectReact(() => {
      const nhId = item?.relation?.neighbourhood?._id
        || item?.relation?.neighbourhood;
      if (!nhId) return;
      (async () => {
        try {
          const { data } = await requests.geo.neighbourhoods.resolve(nhId);
          const chain = data?.data || data || {};
          const label = [chain.neighbourhood?.name, chain.city?.name, chain.country?.name]
            .filter(Boolean)
            .join(', ') || String(nhId);
          setNeighbourhoodOptions((prev) => (
            prev.some((o) => o.value === nhId) ? prev : [{ value: nhId, label }, ...prev]
          ));
        } catch (_) {
          setNeighbourhoodOptions((prev) => (
            prev.some((o) => o.value === nhId) ? prev : [{ value: nhId, label: String(nhId) }, ...prev]
          ));
        }
      })();
    }, [item?._id]);

    return (
      <Controller
        control={control}
        name={name}
        defaultValue={{ relationType: 'in', location: null, region: null, neighbourhood: null }}
        render={({ field }) => {
          const value = field.value || {};
          const nhId = value.neighbourhood?._id || value.neighbourhood;

          const setNeighbourhood = async (selected) => {
            if (!selected) {
              field.onChange({
                relationType: value.relationType || 'in',
                neighbourhood: null, region: null, location: null,
              });
              return;
            }
            let regionId = null;
            try {
              const { data } = await requests.geo.neighbourhoods.resolve(selected.value);
              regionId = (data?.data || data || {}).region?._id || null;
            } catch (_) { /* optional */ }
            field.onChange({
              relationType: value.relationType || 'in',
              neighbourhood: { _id: selected.value },
              region: regionId ? { _id: regionId } : null,
              location: null,
            });
          };

          return (
            <div className="mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                <div className="w-full">
                  <select
                    id="relationType"
                    className="border h-10 px-3 w-full rounded-md border-gray-300 bg-white"
                    value={value.relationType || 'in'}
                    onChange={(e) => field.onChange({ ...value, relationType: e.target.value })}
                  >
                    <option value="in">in</option>
                    <option value="nearby">nearby</option>
                    <option value="around">around</option>
                    <option value="at">at</option>
                  </select>
                </div>
                <div className="w-full">
                  <ReactDropdown
                    options={neighbourhoodOptions}
                    value={nhId ? neighbourhoodOptions.find((opt) => opt.value === nhId) : null}
                    onChange={setNeighbourhood}
                    isMulti={false}
                    placeholder="Search neighbourhood..."
                    isLoading={neighbourhoodLoading}
                    filterOption={() => true}
                    onInputChange={(inputValue) => {
                      if (neighbourhoodSearchTimeoutRef.current) {
                        clearTimeout(neighbourhoodSearchTimeoutRef.current);
                      }
                      neighbourhoodSearchTimeoutRef.current = setTimeout(() => {
                        fetchNeighbourhoods(1, inputValue, true);
                      }, 500);
                    }}
                    onLoadMore={async () => {
                      const next = neighbourhoodPage + 1;
                      if (next <= neighbourhoodPages && !neighbourhoodLoading) {
                        await fetchNeighbourhoods(next, '', false);
                      }
                    }}
                    hasMore={neighbourhoodPage < neighbourhoodPages}
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Select a neighbourhood; region is derived from the geo hierarchy.
              </p>
            </div>
          );
        }}
      />
    );
  };

  const fields = [
    { name: 'name', label: 'Name', type: 'text', required: true, placeholder: 'e.g. Cuisine Type' },
    { name: 'keywords', label: 'Keywords', type: 'keywords-input', required: false },
    { name: 'relation', label: 'Relation & Place', type: 'dropdown', component: <RelationGeoRow /> },
    {
      name: 'mergedKeywordsPreview',
      label: 'Merged Keywords',
      type: 'custom',
      component: (
        item?.mergedKeywords?.length ? (
          <div className="flex flex-wrap gap-2">
            {item.mergedKeywords.map((mk, idx) => (
              <span
                key={idx}
                className="inline-block bg-gray-100 text-gray-800 text-sm px-2 py-1 rounded border border-gray-200"
              >
                {mk}
              </span>
            ))}
          </div>
        ) : (
          <div className="text-sm text-gray-500">No merged keywords yet. Save to generate.</div>
        )
      )
    },
    
    { name: 'status', label: 'Status', type: 'select', required: false, defaultValue: '1', options: [
      { value: '0', label: 'Inactive' },
      { value: '1', label: 'Active' },
      { value: '2', label: 'Suspended' },
    ] },
  ];

  const defaultValues = item ? {
    name: item.name,
    keywords: item.keywords || [],
    status: String(item.status ?? 1),
    relation: item.relation || { relationType: 'in', location: null, region: null, neighbourhood: null },
  } : {
    name: '',
    keywords: [],
    status: '1',
    relation: { relationType: 'in', location: null, region: null, neighbourhood: null },
  };

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">{keywordCategoryId ? 'Edit' : 'Create'} Keyword Category</h2>
      <ReactForm
        fields={fields}
        onSubmit={handleSubmit}
        loading={loading}
        error={error}
        defaultValues={defaultValues}
        submitText={keywordCategoryId ? 'Update' : 'Create'}
      />
    </div>
  );
}


